// Cronograma das parcelas: gera as datas sugeridas e formata para leitura.
//
// Existe para a tela do pedido (Central de Tickets) e a tela da despesa
// (Contas a Pagar) mostrarem O MESMO cronograma para o mesmo pedido, e para as
// duas baterem com o servidor. A conta de mês é a mesma do `_mesAdiante` do
// `dre-engine/handlers/contasPagarAnexos.js`. As duas cópias são mantidas
// iguais pelos MESMOS casos de borda afirmados nos dois testes — 31/01 por
// cinco meses e a virada de ano/bissexto: aqui em `vencimentosParcelas.test.ts`
// e lá em `tests/contas-pagar/vencimentos.test.js`. Mexeu numa, mexa na outra.

export type RitmoVencimento = 'mensal' | 'dias';

// Soma meses SEM Date.setMonth. Pelo setMonth, 31/01 + 1 mês vira 03/03 e
// fevereiro fica sem parcela nenhuma; o dia aqui é limitado ao último do mês.
export function mesAdiante(dataIso: string, meses: number): string {
  const y0 = parseInt(dataIso.slice(0, 4), 10);
  const m0 = parseInt(dataIso.slice(5, 7), 10);
  const dia = parseInt(dataIso.slice(8, 10), 10);
  const total = (m0 - 1) + meses;
  const y = y0 + Math.floor(total / 12);
  const m = (total % 12) + 1;
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(Math.min(dia, ultimo)).padStart(2, '0')}`;
}

export function diasAdiante(dataIso: string, dias: number): string {
  const d = new Date(dataIso + 'T12:00:00');
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

// Datas sugeridas a partir da primeira. `mensal` = todo mês no mesmo dia (o
// padrão: é o que as pessoas combinam); `dias` = de N em N dias corridos.
export function gerarVencimentos(
  primeiro: string,
  n: number,
  ritmo: RitmoVencimento = 'mensal',
  intervaloDias = 30,
): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(primeiro || '') || !(n >= 1)) return [];
  // Mesmo teto do servidor (MAX_PARCELAS). Sem ele, um zero a mais digitado no
  // número de parcelas monta 1.200 seletores de data antes de alguém corrigir.
  const total = Math.min(Math.floor(n), 120);
  return Array.from({ length: total }, (_, p) => (
    ritmo === 'mensal' ? mesAdiante(primeiro, p) : diasAdiante(primeiro, p * intervaloDias)
  ));
}

// '2026-02-30' tem o formato certo e não existe. Quem recusaria seria o
// Postgres, no meio da gravação das parcelas — e aí já sobrou parcela órfã.
export function dataExiste(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return false;
  const y = parseInt(iso.slice(0, 4), 10);
  const m = parseInt(iso.slice(5, 7), 10);
  const d = parseInt(iso.slice(8, 10), 10);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function fmtDataCurta(iso: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '';
}

// Resumo de uma linha para caber ao lado do campo: "05/10, 05/11, 05/12".
// Acima de `teto` datas, corta no meio e mostra a última — quem confere quer
// saber quando começa e quando termina.
export function resumoVencimentos(lista: string[], teto = 4): string {
  const datas = (lista || []).filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d));
  if (!datas.length) return '';
  if (datas.length <= teto) return datas.map(fmtDataCurta).join(', ');
  const cabeca = datas.slice(0, teto - 1).map(fmtDataCurta).join(', ');
  return `${cabeca} … ${fmtDataCurta(datas[datas.length - 1])}`;
}

// As datas batem com o que o servidor aceita? Mesma régua do pré-voo do
// POST /contas-pagar: quantidade certa, formato ISO e estritamente crescentes.
export function conferirVencimentos(lista: string[], parcelas: number): string | null {
  if (!Array.isArray(lista) || lista.length !== parcelas) {
    return `são ${parcelas} parcela(s) e ${Array.isArray(lista) ? lista.length : 0} data(s)`;
  }
  for (let i = 0; i < lista.length; i++) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(lista[i] || '')) return `falta a data da parcela ${i + 1}`;
    if (!dataExiste(lista[i])) return `a parcela ${i + 1} tem uma data que não existe`;
  }
  for (let i = 1; i < lista.length; i++) {
    if (lista[i] <= lista[i - 1]) {
      return `a parcela ${i + 1} vence antes (ou no mesmo dia) da parcela ${i}`;
    }
  }
  return null;
}
